import { useEffect, useRef } from 'react'
import { cn } from '~utils/cn'

type PartialInputProps = Pick<React.ComponentPropsWithoutRef<'input'>, 'className' | 'style'>

interface Props extends PartialInputProps {
  /**
   * full value of the otp input, up to {size} characters
   */
  value: string
  onChange(value: string): void
  /**
   * Number of characters/input for this component
   */
  size?: number
  /**
   * Validation pattern for each input.
   * e.g: /[0-9]{1}/ for digits only or /[0-9a-zA-Z]{1}/ for alphanumeric
   */
  validationPattern?: RegExp
}

/**
 * @version v1.0.0
 * @author Danny
 * @reference https://reacthustle.com/blog/how-to-build-a-react-otp-input-using-daisyui
 */
const Otp = ({
  size = 6,
  // Default validation is digits
  validationPattern = /^[0-9]+$/,
  value,
  onChange,
  className,
  ...props
}: Readonly<Props>) => {
  const firstInputRef = useRef<HTMLInputElement>(null)
  const arr = new Array(size).fill('')

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const elem = e.target
    const val = e.target.value
    // check if the value is valid
    if (!validationPattern.test(val) && val !== '') {
      return
    }

    // change the value of the upper state using onChange
    const valueArr = value.split('')
    valueArr[index] = val
    const newVal = valueArr.join('').slice(0, 6)
    onChange(newVal)

    // focus the next element if there's a value
    if (val) {
      const next = elem.nextElementSibling as HTMLInputElement | null
      next?.focus()
    }
  }

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const current = e.currentTarget
    if (e.key === 'ArrowLeft' || e.key === 'Backspace') {
      const prev = current.previousElementSibling as HTMLInputElement | null
      prev?.focus()
      prev?.setSelectionRange(0, 1)
      return
    }

    if (e.key === 'ArrowRight') {
      const prev = current.nextSibling as HTMLInputElement | null
      prev?.focus()
      prev?.setSelectionRange(0, 1)
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const val = e.clipboardData.getData('text').substring(0, size)
    onChange(val)
  }

  useEffect(() => {
    firstInputRef.current?.focus()
  }, [])

  return (
    <div className="flex flex-gap-x-1 lg:flex-gap-x-2">
      {arr.map((_, index) => (
        <input
          {...props}
          ref={index === 0 ? firstInputRef : null}
          key={index}
          className={cn(
            'h-16 w-12 flex-1 rounded-2xl border bg-white border-solid border-white px-0 text-center text-gray-900 outline-none text-title-1 focus:border-gray-900 dark:bg-gray-800 dark:border-gray-800 dark:text-white',
            className
          )}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern={validationPattern.source}
          maxLength={size}
          value={value.at(index) ?? ''}
          onChange={e => handleInputChange(e, index)}
          onKeyUp={handleKeyUp}
          onPaste={handlePaste}
        />
      ))}
    </div>
  )
}

export default Otp
